import React, { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Phone, Loader2, CheckCircle, Calendar } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const WHATSAPP_NUMBER = "919818065496";
const CAL_LINK = "https://cal.com/elite-school-advisory/30min";

const grades = [
  "Year 6 (Age 10-11)",
  "Year 7 (Age 11-12)",
  "Year 8 (Age 12-13)",
  "Year 9 (Age 13-14)",
  "Year 10 (Age 14-15)",
  "Year 11 (Age 15-16)",
  "Year 12 (Age 16-17)",
  "Year 13 (Age 17-18)",
];

const enquiryTypes = [
  "Boarding School Admissions",
  "Summer School",
  "Immersion Programme",
];

const destinations = [
  "United Kingdom",
  "United States",
  "Switzerland",
  "Canada",
  "Multiple Destinations",
  "Not Sure Yet",
];

const timelines = [
  "Immediate (Next 6 months)",
  "Next Academic Year",
  "1-2 Years Away",
  "Just Exploring",
];

const FinalCTA = () => {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    enquiry_type: "",
    child_grade: "",
    preferred_destinations: "",
    timeline: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/leads`, formData);
      setIsSubmitted(true);
    } catch (err) {
      setError("Something went wrong. Please try again or contact us via WhatsApp.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const openWhatsApp = () => {
    const message = encodeURIComponent(
      "Hello, I would like to enquire about Elite School Advisory services for my child."
    );
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${message}`, "_blank");
  };

  const openCalendar = () => {
    window.open(CAL_LINK, "_blank");
  };

  return (
    <section
      id="contact"
      className="py-20 md:py-28 cta-bg relative overflow-hidden"
    >
      {/* Decorative elements */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#C9A227]/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-white/5 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />

      <div className="container mx-auto px-4 md:px-8 max-w-7xl relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-sm uppercase tracking-[0.2em] font-semibold text-[#C9A227] mb-4">
              Take the First Step
            </p>
            <h2
              className="text-3xl md:text-4xl lg:text-5xl font-medium text-white tracking-tight mb-6"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Your Child Deserves the{" "}
              <span className="text-[#C9A227]">Right School</span>
            </h2>
            <p className="text-lg text-white/80 leading-relaxed mb-8">
              The boarding school journey is significant. Let us help you navigate
              it with clarity, confidence, and care. Book a private strategy call
              to begin.
            </p>

            <div className="space-y-4">
              <div className="flex items-center gap-3 text-white/70">
                <CheckCircle className="w-5 h-5 text-[#C9A227]" />
                <span>Complimentary 30-minute diagnostic call</span>
              </div>
              <div className="flex items-center gap-3 text-white/70">
                <CheckCircle className="w-5 h-5 text-[#C9A227]" />
                <span>No obligation, no pressure</span>
              </div>
              <div className="flex items-center gap-3 text-white/70">
                <CheckCircle className="w-5 h-5 text-[#C9A227]" />
                <span>Personalised assessment of your situation</span>
              </div>
            </div>

            {/* Direct Booking & WhatsApp Options */}
            <div className="mt-10 pt-8 border-t border-white/20 space-y-4">
              <p className="text-white/60 text-sm mb-4">
                Prefer to book directly or message us?
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  onClick={openCalendar}
                  className="bg-[#C9A227] text-white hover:bg-[#B08D1F] rounded-[2px] px-6 py-5 font-medium"
                  data-testid="cal-booking-btn"
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  Book on Calendar
                </Button>
                <Button
                  onClick={openWhatsApp}
                  variant="outline"
                  className="border-2 border-[#C9A227] text-[#C9A227] hover:bg-[#C9A227] hover:text-white bg-transparent rounded-[2px] px-6 py-5"
                  data-testid="whatsapp-btn"
                >
                  <Phone className="w-4 h-4 mr-2" />
                  Message on WhatsApp
                </Button>
              </div>
            </div>
          </motion.div>

          {/* Right Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-white p-8 md:p-10 rounded-[4px] shadow-2xl"
          >
            {isSubmitted ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 rounded-full bg-[#C9A227]/20 flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-8 h-8 text-[#C9A227]" />
                </div>
                <h3
                  className="text-2xl font-medium text-[#1A202C] mb-4"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  Thank You
                </h3>
                <p className="text-[#4A5568] mb-6">
                  We have received your enquiry and will be in touch within 24
                  hours to schedule your strategy call.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Button
                    onClick={openCalendar}
                    className="bg-[#2B4C7E] text-white hover:bg-[#1E3A60] rounded-[2px]"
                    data-testid="cal-after-submit"
                  >
                    <Calendar className="w-4 h-4 mr-2" />
                    Book a Slot Now
                  </Button>
                  <Button
                    onClick={openWhatsApp}
                    variant="outline"
                    className="border-2 border-[#2B4C7E] text-[#2B4C7E] hover:bg-[#2B4C7E] hover:text-white bg-transparent rounded-[2px]"
                    data-testid="whatsapp-after-submit"
                  >
                    Or reach us on WhatsApp
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <h3
                  className="text-xl md:text-2xl font-medium text-[#1A202C] mb-6"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  Book a Private Strategy Call
                </h3>

                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <Input
                      type="text"
                      name="name"
                      placeholder="Your Name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="h-12 bg-white border-b-2 border-[#E2E8F0] focus:border-[#2B4C7E] rounded-none px-4"
                      data-testid="form-name"
                    />
                  </div>

                  <div>
                    <Input
                      type="tel"
                      name="phone"
                      placeholder="Phone Number"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="h-12 bg-white border-b-2 border-[#E2E8F0] focus:border-[#2B4C7E] rounded-none px-4"
                      data-testid="form-phone"
                    />
                  </div>

                  <div>
                    <Input
                      type="email"
                      name="email"
                      placeholder="Email Address"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="h-12 bg-white border-b-2 border-[#E2E8F0] focus:border-[#2B4C7E] rounded-none px-4"
                      data-testid="form-email"
                    />
                  </div>

                  <div>
                    <Select
                      onValueChange={(value) =>
                        handleSelectChange("enquiry_type", value)
                      }
                    >
                      <SelectTrigger
                        className="h-12 bg-white border-b-2 border-[#E2E8F0] focus:border-[#2B4C7E] rounded-none px-4"
                        data-testid="form-enquiry-type"
                      >
                        <SelectValue placeholder="Type of Enquiry" />
                      </SelectTrigger>
                      <SelectContent>
                        {enquiryTypes.map((type) => (
                          <SelectItem key={type} value={type}>
                            {type}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Select
                      onValueChange={(value) =>
                        handleSelectChange("child_grade", value)
                      }
                    >
                      <SelectTrigger
                        className="h-12 bg-white border-b-2 border-[#E2E8F0] focus:border-[#2B4C7E] rounded-none px-4"
                        data-testid="form-grade"
                      >
                        <SelectValue placeholder="Child's Current Grade" />
                      </SelectTrigger>
                      <SelectContent>
                        {grades.map((grade) => (
                          <SelectItem key={grade} value={grade}>
                            {grade}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Select
                      onValueChange={(value) =>
                        handleSelectChange("preferred_destinations", value)
                      }
                    >
                      <SelectTrigger
                        className="h-12 bg-white border-b-2 border-[#E2E8F0] focus:border-[#2B4C7E] rounded-none px-4"
                        data-testid="form-destinations"
                      >
                        <SelectValue placeholder="Preferred Destinations" />
                      </SelectTrigger>
                      <SelectContent>
                        {destinations.map((dest) => (
                          <SelectItem key={dest} value={dest}>
                            {dest}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Select
                      onValueChange={(value) =>
                        handleSelectChange("timeline", value)
                      }
                    >
                      <SelectTrigger
                        className="h-12 bg-white border-b-2 border-[#E2E8F0] focus:border-[#2B4C7E] rounded-none px-4"
                        data-testid="form-timeline"
                      >
                        <SelectValue placeholder="When are you looking to start?" />
                      </SelectTrigger>
                      <SelectContent>
                        {timelines.map((timeline) => (
                          <SelectItem key={timeline} value={timeline}>
                            {timeline}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {error && (
                    <p className="text-red-500 text-sm" data-testid="form-error">
                      {error}
                    </p>
                  )}

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-[#2B4C7E] text-white hover:bg-[#1E3A60] rounded-[2px] px-8 py-6 text-base font-medium shadow-lg hover:shadow-xl transition-all duration-300 mt-4"
                    data-testid="form-submit-btn"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        Book a Private Strategy Call
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </>
                    )}
                  </Button>
                </form>

                <p className="text-xs text-[#4A5568] text-center mt-6">
                  By submitting, you agree to our Privacy Policy. We will never
                  share your information.
                </p>
              </>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default FinalCTA;
